import { useMemo, useRef, useState } from 'react';

import { Close, Refresh } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { format, formatISO, parseISO } from 'date-fns';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_RowVirtualizer,
  MRT_SortingState,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { useQuery } from 'react-query';
import { getSharedWalletUsers } from 'src/services/wallet';
import { SharedWalletUser } from 'src/types/wallet-monitor';

type SharedTableProps = {
  walletId: string | number;
  removeSharedWallet: (email: string) => void;
  renewSharedWallet: (email: string) => void;
};

export function SharedTable({
  walletId,
  removeSharedWallet,
  renewSharedWallet,
}: SharedTableProps) {
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['shared-wallet-users', walletId],
    queryFn: () => getSharedWalletUsers(Number(walletId)),
  });

  const columns = useMemo<MRT_ColumnDef<SharedWalletUser>[]>(
    () => [
      {
        accessorKey: 'recipient',
        header: 'Email',
        size: 180,
        Cell: ({ cell }) => {
          return (
            <Typography variant="body2">{cell.getValue<string>()}</Typography>
          );
        },
      },
      {
        accessorKey: 'expiresAt',
        header: 'Expira em',
        size: 90,
        Cell: ({ cell }) => {
          const date = format(parseISO(cell.getValue<string>()), 'dd/MM/yyyy');
          return <Typography variant="body2">{date}</Typography>;
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 80,
        Cell: ({ cell }) => {
          const isExpired =
            formatISO(new Date(cell.row.original?.expiresAt)) <
            formatISO(new Date());

          const status = cell.getValue<string>();

          const getStatus = () => {
            if (isExpired) {
              return 'Expirado';
            }
            if (status === 'active' && !isExpired) {
              return 'Ativo';
            }
            if (status === 'cancelled' && !isExpired) {
              return 'Cancelado';
            }
            return '---';
          };

          return <Typography variant="body2">{getStatus()}</Typography>;
        },
      },
      {
        accessorKey: 'actions',
        header: 'Ações',
        size: 120,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnFilterModes: false,
        Cell: ({ cell }) => {
          const isExpired =
            formatISO(new Date(cell.row.original?.expiresAt)) <
            formatISO(new Date());

          return (
            <Box display="flex" gap={1} justifyContent="center">
              {isExpired && (
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() =>
                    renewSharedWallet(cell.row.original?.guest.email)
                  }
                  title="Renovar">
                  <Refresh fontSize="small" />
                </IconButton>
              )}
              <IconButton
                size="small"
                color="error"
                onClick={() =>
                  removeSharedWallet(cell.row.original?.guest.email)
                }
                title="Remover">
                <Close fontSize="small" />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: data?.data ?? [],
    enableBottomToolbar: false,
    enableGlobalFilterModes: true,
    enablePagination: false,
    enableRowNumbers: false,
    enableRowVirtualization: true,
    muiTableContainerProps: {
      sx: {
        maxHeight: '500px',
      },
    },
    onSortingChange: setSorting,
    state: { isLoading, sorting },
    rowVirtualizerInstanceRef,
    rowVirtualizerOptions: { overscan: 5 },
    enableColumnActions: false,
    enableGlobalFilter: true,
    enableColumnDragging: false,
    enableDensityToggle: false,
    enableExpandAll: false,
    enableFullScreenToggle: false,
    enableEditing: false,
    enableHiding: false,
    enableTopToolbar: true,
    enableFilters: true,
    enableColumnFilterModes: true,
    enableFilterMatchHighlighting: true,
    localization: MRT_Localization_PT_BR,
    muiTablePaperProps: {
      elevation: 0,
    },
    muiTableHeadRowProps: {
      sx: (theme) => ({
        backgroundColor: theme.palette.grey[200],
      }),
    },
  });

  return (
    <Box>
      <MaterialReactTable table={table} />
    </Box>
  );
}
